/**
 * Advanced Static/Dynamic Content Selector
 * 
 * Kompakte, moderne Komponente für die Auswahl zwischen statischen und dynamischen Inhalten
 * mit vollständiger Platzhalter-Unterstützung. Folgt dem Design-System der Property-Komponenten.
 */

import React from 'react';
import { Label } from '@/ui-components/ui/label';
import { Button } from '@/ui-components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui-components/ui/popover';

import { MediaType, StaticOrDynamic } from '../../../core/template.schemas';
import { StaticInputRenderer } from './StaticInputComponents';

// Refactored components
import { ModeToggle } from './ModeToggle';
import { MediaTypeSelector } from './MediaTypeSelector';
import { DynamicSourceSelector } from './DynamicSourceSelector';
import { FormatSelector } from './FormatSelector';
import { useAdvancedSelector } from './useAdvancedSelector';
import { getDefaultFormat } from '../../registry/media/media.registered';
import { useTemplatePermissions } from '../../hooks/useTemplatePermissions';

interface AdvancedStaticDynamicSelectorProps {
  /** Current value */
  value: StaticOrDynamic<unknown>;
  /** Change callback */
  onChange: (value: StaticOrDynamic<unknown> | null, meta?: { width?: number; height?: number }) => void;
  /** Compatible media types for this attribute */
  compatibleMediaTypes: MediaType[];
  /** Label for the selector */
  label?: string;
  /** Whether field is required */
  required?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Callback to open placeholder panel */
  onOpenSlotPanel?: () => void;
  /** Optional allowed MIME types for image selection */
  allowedMimeTypes?: string[];
}

/**
 * Advanced selector for static/dynamic content with full placeholder support
 */
export const AdvancedStaticDynamicSelector: React.FC<AdvancedStaticDynamicSelectorProps> = ({
  value,
  onChange,
  compatibleMediaTypes,
  label,
  required = false,
  className = '',
  allowedMimeTypes,
}) => {
  const { canManageStore } = useTemplatePermissions();
  const {
    mode,
    selectedMediaType,
    staticValue,
    selectedSourceKind,
    selectedSlotId,
    selectedSlotAttributePath,
    selectedSelectorId,
    selectedOverrideKey,
    format,
    handleModeChange,
    handleSlotSelect,
    handleStaticValueChange,
    handleSlotAttributeSelect,
    handleFormatChange,
    handleSourceKindChange,
    handleSelectorIdChange,
    handleCreateSlot,
    handleMediaTypeChange,
    defaultDisplayValue,
    handleDefaultValueChange,
    handleClearDefaultValue,
    handleOverrideKeyChange
  } = useAdvancedSelector({
    value,
    onChange,
    compatibleMediaTypes
  });

  const safeOnSourceKindChange = (kind: 'slot' | 'selector' | 'override') => {
    if (kind === 'override' && !canManageStore) return;
    handleSourceKindChange(kind);
  };

  return (
    <div className={`space-y-3 p-3 rounded-md border ${
      mode === 'dynamic' 
        ? 'bg-blue-50/20 border-blue-200/60' 
        : 'bg-gray-50/20 border-gray-200/60'
    } ${className}`}>
      
      {/* Mode Toggle */}
      {label !== undefined && (
        <ModeToggle
          mode={mode}
          label={label}
          required={required}
          onModeChange={handleModeChange}
        />
      )}

      {/* Static Mode */}
      {mode === 'static' && (
        <div className="space-y-2">
          {/* Media Type Selection */}
          <MediaTypeSelector
            selectedMediaType={selectedMediaType}
            compatibleMediaTypes={compatibleMediaTypes}
            onMediaTypeChange={handleMediaTypeChange}
          />

          {/* Static Value Input */}
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Wert</Label>
            <StaticInputRenderer
              value={staticValue}
              onChange={handleStaticValueChange}
              mediaType={selectedMediaType}
              className=""
              allowedMimeTypes={allowedMimeTypes}
            />
          </div>
        </div>
      )}

      {/* Dynamic Mode */}
      {mode === 'dynamic' && (
        <div className="space-y-2">
          {/* Media Type Selection */}
          <MediaTypeSelector
            selectedMediaType={selectedMediaType}
            compatibleMediaTypes={compatibleMediaTypes}
            onMediaTypeChange={handleMediaTypeChange}
          />

          {/* Placeholder Selection */}
          <DynamicSourceSelector
            selectedSlotId={selectedSlotId}
            selectedSlotAttributePath={selectedSlotAttributePath}
            compatibleMediaTypes={compatibleMediaTypes}
            selectedMediaType={selectedMediaType}
            selectedSourceKind={selectedSourceKind}
            selectedSelectorId={selectedSelectorId}
            selectedOverrideKey={selectedOverrideKey}
            isInternal={canManageStore}
            onSlotSelect={handleSlotSelect}
            onSlotAttributeSelect={handleSlotAttributeSelect}
            onCreateSlot={handleCreateSlot}
            onSourceKindChange={safeOnSourceKindChange}
            onSelectorIdChange={handleSelectorIdChange}
            onOverrideKeyChange={handleOverrideKeyChange}
          />

          {/* Default Value */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500 mb-1 block">Standardwert</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-full h-6 text-xs justify-start">
                  Standardwert festlegen
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <div className="space-y-3">
                  <StaticInputRenderer
                    value={defaultDisplayValue}
                    onChange={handleDefaultValueChange}
                    mediaType={selectedMediaType}
                    className=""
                    allowedMimeTypes={allowedMimeTypes}
                  />
                  <Button variant="outline" size="sm" className="h-6 text-xs w-full" onClick={handleClearDefaultValue}>
                    Zurücksetzen
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

        </div>
      )}

      {/* Format Configuration */}
      <FormatSelector
        selectedMediaType={selectedMediaType}
        format={format ?? value.format ?? getDefaultFormat(selectedMediaType)}
        onFormatChange={handleFormatChange}
      />
    </div>
  );
};
